import { Reducer } from 'redux';
import { ECustomerTypeView } from '../user/types';
import {
  IInvoiceData,
  IInvoiceCreditsResponse,
  IInvoicesResponse,
  InvoicesState,
  Types,
  IInvoiceBilletResponse,
  IInvoiceXMLResponse
} from './types';
import moment from 'moment';
import { IDownloadNFEResponse } from '../long-steel-orders/types';

const startDate = moment().subtract(30, 'days');
const endDate = moment();

const INITIAL_STATE: InvoicesState = {
  receivers: [],
  initialInputValues: {
    selectedCustomerId: '',
    startDate: startDate,
    endDate: endDate
  },
  loading: false,
  error: false,
  bpError: false,
  lastLoadTime: undefined,

  invoiceCreditsState: {
    loading: false,
    error: false,
    invoices: [],
    creditSum: '',
    customerId: ''
  }
};

type TCustomAction = {
  type: Types;
  payload: {
    invoicesResponse: IInvoicesResponse;
    customerViewType: ECustomerTypeView;

    bpError: boolean;

    invoiceCreditsResponse: IInvoiceCreditsResponse;

    invoiceItem: IInvoiceData;
    filteredInvoices: IInvoiceData[];
    oldInvoices: IInvoiceData[];
    downloadNFEResponse: IDownloadNFEResponse;
    downloadXmlResponse: IInvoiceXMLResponse;
    invoiceBilletResponse: IInvoiceBilletResponse;

    customerId: string;
    isUserAdmin: boolean;
    startDate: moment.Moment;
    endDate: moment.Moment;
  };
};

const reducer: Reducer<InvoicesState, TCustomAction> = (
  state = INITIAL_STATE,
  action
) => {
  switch (action.type) {
    case Types.LOAD_DUPLICATES:
      return {
        ...state,
        invoices: [],
        receivers: [],
        loading: true,
        error: false,
        bpError: false,
        lastLoadTime: undefined,
        initialInputValues: {
          selectedCustomerId: action.payload.customerId,
          startDate: action.payload.startDate,
          endDate: action.payload.endDate
        }
      };

    case Types.FILTER_DUPLICATES_BY_DATE:
      return { ...state, loading: true };

    case Types.FILTER_DUPLICATES_BY_DATE_SUCCESS:
      return {
        ...state,
        invoices: action.payload.filteredInvoices,
        loading: false
      };

    case Types.LOAD_OLD_INVOICES:
      return { ...state, oldInvoices: action.payload.oldInvoices };

    case Types.LOAD_DUPLICATES_SUCCESS:
      return {
        ...state,
        ...action.payload.invoicesResponse,
        invoices: action.payload.invoicesResponse.invoices,
        loading: false,
        error: false
      };

    case Types.LOAD_DUPLICATES_FAILURE:
      return {
        ...state,
        invoices: [],
        receivers: [],
        loading: false,
        error: true,
        bpError: action.payload.bpError
      };

    // INVOICE CREDITS
    case Types.LOAD_INVOICE_CREDITS:
      return {
        ...state,
        invoiceCreditsState: {
          ...state.invoiceCreditsState,
          customerId: action.payload.customerId,
          invoices: [],
          creditSum: '',
          loading: true,
          error: false
        }
      };

    case Types.LOAD_INVOICE_CREDITS_SUCCESS:
      return {
        ...state,
        invoiceCreditsState: {
          ...state.invoiceCreditsState,
          invoices: action.payload.invoiceCreditsResponse.invoices,
          creditSum: action.payload.invoiceCreditsResponse.creditSum,
          loading: false,
          error: false
        }
      };

    case Types.LOAD_INVOICE_CREDITS_FAILURE:
      return {
        ...state,
        invoiceCreditsState: {
          ...state.invoiceCreditsState,
          loading: false,
          error: true
        }
      };
    //

    // NF
    case Types.LOAD_NF_FILE:
      return {
        ...state,
        invoices: setNFStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          true,
          false
        )
      };

    case Types.LOAD_NF_FILE_SUCCESS:
      return {
        ...state,
        invoices: setNFStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.downloadNFEResponse
        )
      };

    case Types.LOAD_NF_FILE_FAILURE:
      return {
        ...state,
        invoices: setNFStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          true
        )
      };

    case Types.DOWNLOAD_NF_FILE:
      return {
        ...state,
        invoices: setNFStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.invoiceItem.nfState?.downloadNFEResponse,
          true
        )
      };
    //

    // NF
    case Types.LOAD_INVOICE_BILLET:
      return {
        ...state,
        invoices: setBilletStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          true,
          false
        )
      };

    case Types.LOAD_INVOICE_BILLET_SUCCESS:
      return {
        ...state,
        invoices: setBilletStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.invoiceBilletResponse
        )
      };

    case Types.LOAD_INVOICE_BILLET_FAILURE:
      return {
        ...state,
        invoices: setBilletStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          true
        )
      };

    case Types.DOWNLOAD_INVOICE_BILLET:
      return {
        ...state,
        invoices: setBilletStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.invoiceItem.billetState?.invoiceBilletResponse,
          true
        )
      };
    //

    case Types.LOAD_INVOICE_XML:
      return {
        ...state,
        invoices: setXMLStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          true,
          false
        )
      };

    case Types.LOAD_INVOICE_XML_SUCCESS:
      return {
        ...state,
        invoices: setXMLStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.downloadXmlResponse
        )
      };

    case Types.LOAD_INVOICE_XML_FAILURE:
      return {
        ...state,
        invoices: setXMLStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          true
        )
      };

    case Types.DOWNLOAD_INVOICE_XML:
      return {
        ...state,
        invoices: setXMLStateInDuplicate(
          state.invoices || [],
          action.payload.invoiceItem,
          false,
          false,
          action.payload.invoiceItem.xmlState.invoiceXmlResponse,
          true
        )
      };

    case Types.CLEAR_INVOICES_STATE:
      return INITIAL_STATE;

    default:
      return state;
  }
};

const setNFStateInDuplicate = (
  items: IInvoiceData[],
  selectedItem: IInvoiceData,
  loading: boolean,
  error: boolean,
  downloadNFEResponse?: IDownloadNFEResponse,
  alreadyDownloaded?: boolean
): IInvoiceData[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.invoiceNumber === selectedItem.invoiceNumber
  );

  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IInvoiceData).nfState = {
      ...(items[selectedItemIdx] as IInvoiceData).nfState,
      loading: loading,
      error: error,
      downloadNFEResponse,
      alreadyDownloaded,
      canDownloadNF: true
    };
  }

  return items;
};

const setBilletStateInDuplicate = (
  items: IInvoiceData[],
  selectedItem: IInvoiceData,
  loading: boolean,
  error: boolean,
  invoiceBilletResponse?: IInvoiceBilletResponse,
  alreadyDownloaded?: boolean
): IInvoiceData[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.invoiceNumber === selectedItem.invoiceNumber
  );

  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IInvoiceData).billetState = {
      ...(items[selectedItemIdx] as IInvoiceData).billetState,
      loading: loading,
      error: error,
      invoiceBilletResponse,
      alreadyDownloaded
    };
  }

  return items;
};

const setXMLStateInDuplicate = (
  items: IInvoiceData[],
  selectedItem: IInvoiceData,
  loading: boolean,
  error: boolean,
  invoiceXmlResponse?: IInvoiceXMLResponse,
  alreadyDownloaded?: boolean
): IInvoiceData[] => {
  const selectedItemIdx = items.findIndex(
    (item) => item.invoiceNumber === selectedItem.invoiceNumber
  );

  if (selectedItemIdx !== -1) {
    (items[selectedItemIdx] as IInvoiceData).xmlState = {
      ...(items[selectedItemIdx] as IInvoiceData).xmlState,
      loading: loading,
      error: error,
      invoiceXmlResponse,
      alreadyDownloaded
    };
  }

  return items;
};

export default reducer;
